import React, { useRef, useState, useContext, useEffect } from 'react'
import { FileUpload } from 'primereact/fileupload'
import { ProgressBar } from 'primereact/progressbar'
import { Tag } from 'primereact/tag'
import { useTranslation } from 'react-i18next'
import { LoadingContext } from '../M_Utils'
import PropTypes from 'prop-types'
import { M_Button } from '../M_Button'

export default function M_UploadFiles({ maxSize, multipleFiles, nameURL, onFilesChange, bodyImage, disabled, transformFiles, initialFiles }) {
    const [files, setFiles] = useState([])
    const [totalSize, setTotalSize] = useState(0)
    const fileUploadRef = useRef(null)
    const { t } = useTranslation()
    const ctx = useContext(LoadingContext)
    const formattedMaxSize = fileUploadRef.current ? fileUploadRef.current.formatSize(maxSize) : '0 B'

    const progressBarColor = ctx.primaryColor || 'var(--color-black)'

    useEffect(() => {
        if (initialFiles && initialFiles.length >= 0) {
            const transformed = transformFiles ? transformFiles(initialFiles) : initialFiles
            setFiles(transformed)
            if (onFilesChange) onFilesChange(transformed)
        }
    }, [JSON.stringify(initialFiles)])

    useEffect(() => {
        //função para lidar com o evento de colar arquivos
        const handlePaste = async (event) => {
            const clipboardItems = event.clipboardData?.items || []

            const filesFromClipboard = []
            for (const item of clipboardItems) {
                if (item.kind === 'file') {
                    const file = item.getAsFile()
                    if (file) {
                        file.objectURL = URL.createObjectURL(file)
                        filesFromClipboard.push(file)
                    }
                }
            }

            if (filesFromClipboard.length > 0) {
                onTemplateSelect({ files: filesFromClipboard })
            }
        }


        window.addEventListener('paste', handlePaste)
        return () => {
            window.removeEventListener('paste', handlePaste)
        }
    }, [files, totalSize])



    const updateFiles = (newFiles) => {
        const transformedFiles = transformFiles ? transformFiles([...newFiles]) : [...newFiles]
        setFiles(transformedFiles)
        if (onFilesChange) {
            onFilesChange(transformedFiles)
        }
    }

    const onTemplateSelect = (e) => {
        let _totalSize = totalSize
        let newFiles = [...files]

        const readFileAsBase64 = (file, resolve, reject) => {
            const reader = new FileReader()
            reader.onload = () =>
                resolve({
                    ...file,
                    id: `${file.name}-${Date.now()}`,
                    Name: file.name,
                    ImageBase64: reader.result,
                    objectURL: URL.createObjectURL(file), // <- Adicionado aqui
                })
            reader.onerror = (error) => reject(new Error(error))
            reader.readAsDataURL(file)
        }

        const convertToBase64 = (file) => {
            return new Promise((resolve, reject) => readFileAsBase64(file, resolve, reject))
        }

        const processFiles = async () => {
            const duplicateNames = []

            const base64Files = await Promise.all(
                Object.keys(e.files).map(async (key) => {
                    const file = e.files[key]

                    // Validação de nomes duplicados
                    if (newFiles.some((existingFile) => existingFile.Name === file.name)) {
                        duplicateNames.push(file.name)
                        return null
                    }

                    _totalSize += file.size || 0
                    const base64File = await convertToBase64(file)
                    return base64File
                })
            )

            const validFiles = base64Files.filter((file) => file !== null)

            if (duplicateNames.length > 0) {
                ctx.toastMessages('warning', '', `${t('DuplicateFile')} ${duplicateNames.join(', ')}`
                )
            }

            if (_totalSize > maxSize) {
                ctx.toastMessages('error', '', `${t('SizeFilesError')} ${formattedMaxSize}`)
                return
            }

            newFiles = [...newFiles, ...validFiles]
            setTotalSize(_totalSize)
            updateFiles(newFiles)
        }

        processFiles().catch((error) => {
            console.error('Error converting to base64:', error)
        })
    }


    const onTemplateRemove = (file, callback) => {
        const newFiles = files.filter((f) => !(f.Name === file.name))
        setTotalSize(prevTotalSize => prevTotalSize - (file.size || 0))
        updateFiles(newFiles)
        callback()
    }


    const onTemplateClear = () => {
        setTotalSize(0)
        updateFiles([])
    }

    const headerTemplate = (options) => {
        const { className, chooseButton, cancelButton } = options
        const value = (totalSize / maxSize) * 100
        const formattedTotalSize = fileUploadRef.current ? fileUploadRef.current.formatSize(totalSize) : '0 B'

        return (
            <div className={className} style={{ backgroundColor: 'transparent', display: 'flex', alignItems: 'center', padding: '5px' }}>
                <div style={{ width: '100%' }}>
                    {chooseButton}
                    {cancelButton}
                </div>
                <div>
                    <span>{formattedTotalSize} / {formattedMaxSize}</span>
                    <ProgressBar value={value} color={progressBarColor} showValue={false} style={{ width: '10rem', height: '12px' }} />
                </div>
            </div>
        )
    }

    const truncateText = (text, maxLength) => {
        if (text.length > maxLength) {
            return text.substring(0, maxLength) + '...'
        }
        return text
    }

    const itemTemplate = (file, value) => {
        const fileExtension = file.name.toLowerCase()
        const getIcon = () => {
            switch (true) {
                case fileExtension.endsWith('.pdf'):
                    return "/UploadFilesImages/pdf.png"
                case fileExtension.endsWith('.txt'):
                    return "/UploadFilesImages/txt.png"
                case fileExtension.endsWith('.xlsx') || fileExtension.endsWith('.csv'):
                    return "/UploadFilesImages/excel.png"
                case fileExtension.endsWith('.pptx'):
                    return "/UploadFilesImages/powerpoint.png"
                case fileExtension.endsWith('.docx') || fileExtension.endsWith('.odt'):
                    return "/UploadFilesImages/office.png"
                case fileExtension.endsWith('.rar') || fileExtension.endsWith('.zip'):
                    return "/UploadFilesImages/zip.png"
                case fileExtension.endsWith('.eml'):
                    return "/UploadFilesImages/email.png"
                default:
                    return file.objectURL || "/UploadFilesImages/file.png" // <- fallback se não tiver objectURL
            }
        }


        return (
            <div style={{ display: 'flex', alignItems: 'center' }}>
                <div style={{ width: '40%', display: 'flex', alignItems: 'center' }}>
                    <img
                        src={getIcon()}
                        alt={file.name}
                        role="presentation"
                        width={40}
                        style={{ marginRight: '10px' }}
                    />
                    <span style={{ textAlign: 'left', fontSize: '1em' }}>
                        {/* {file.name} */}
                        {truncateText(file.name, 40)}
                        <br />
                        <small style={{ marginLeft: '10px' }}>{new Date().toLocaleDateString()}</small>
                    </span>
                </div>
                <div style={{ width: '40%' }}>
                    {bodyImage ? bodyImage(file) : null}
                </div>
                <div style={{ width: '20%', display: 'flex', justifyContent: 'right', alignItems: 'center' }}>
                    <Tag value={value.formatSize} severity="warning" style={{ marginRight: '20px', fontSize: '15px' }} />
                    <M_Button
                        icon={<img
                            src="/icons/svgsDaNet/trash-2.svg"
                            alt="Delete Icon"
                            style={{ filter: 'invert(16%) sepia(66%) saturate(5073%) hue-rotate(347deg) brightness(74%) contrast(116%)', width: '25px', cursor: 'pointer' }}
                        />}
                        onClick={() => onTemplateRemove(file, value.onRemove)}
                    />

                </div>
            </div>
        )
    }

    const emptyTemplate = () => (
        <div style={{ width: '100%', display: 'flex', justifyContent: 'center' }}>
            <div>
                <i className="pi pi-image" style={{ fontSize: '3em', borderRadius: '50%', color: 'var(--surface-d)', width: '100%', display: 'flex', justifyContent: 'center' }}></i>
                <div style={{ fontSize: '1em', color: 'var(--color-grey)' }}>
                    {t("DragAnDrop")}
                </div>
            </div>
        </div>
    )

    const chooseOptions = { icon: 'pi pi-fw pi-images', iconOnly: true, style: { backgroundColor: 'var(--color-blue)', width: '20px', height: '20px', padding: '20px', borderRadius: '50%', border: '1px solid var(--color-blue)' } }
    const cancelOptions = { icon: 'pi pi-fw pi-times', iconOnly: true, style: { backgroundColor: 'var(--color-red-dark)', width: '20px', height: '20px', padding: '20px', borderRadius: '50%', border: '1px solid var(--color-red-dark)' } }

    return (
        <div>
            <FileUpload
                ref={fileUploadRef}
                name={nameURL}
                multiple={multipleFiles || false}
                maxFileSize={maxSize || 10000000}
                onSelect={onTemplateSelect}
                onError={onTemplateClear}
                onClear={onTemplateClear}
                headerTemplate={headerTemplate}
                itemTemplate={itemTemplate}
                emptyTemplate={emptyTemplate}
                chooseOptions={chooseOptions}
                cancelOptions={cancelOptions}
                disabled={disabled || false}
            />
        </div>
    )
}

M_UploadFiles.propTypes = {
    maxSize: PropTypes.number,
    multipleFiles: PropTypes.bool,
    nameURL: PropTypes.string,
    onFilesChange: PropTypes.func,
    bodyImage: PropTypes.func,
    transformFiles: PropTypes.func
}
